import * as React from 'react';
import { Component } from 'react';
import { observable, computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { isDisabled } from '../utils/customPropStyles';
import get from 'lodash/get';
import Translate from './Translate';
import FundManagerImage from './FundManagerImage';
import FundManagerContent from './FundManagerContent';
import Icon from './Icon';
import { DataStore } from '../stores';
import { FundManager, FundManagerExtended } from '../models/entity';
import { FundManagers as FundManagersConfig } from '../models/configuration/components/FundManagers';
import { ShowExtended } from '../models/configuration/ShowExtended';
import { Link } from '../models/configuration/Link';

const FundManagersWrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

FundManagersWrapper.defaultProps = {};

const FundManagersHeading = styled<any, any>('h2')`
    ${propStyles};
`;

FundManagersHeading.defaultProps = {
    color: 'body',
    fontFamily: 'heading',
    fontWeight: 'bold',
    fontSize: 'md',
};

const FundManagersNavArrows = styled<any, any>('li')`
    ${propStyles};
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    opacity: 0;
    transition: opacity 300ms linear;
`;

FundManagersNavArrows.defaultProps = {};

const FundManagersList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    &:hover {
        ${FundManagersNavArrows} {
            opacity: 1;
        }
    }
`;

FundManagersList.defaultProps = {
    position: 'relative',
    display: 'flex',
    alignItems: ['flex-start', 'center'],
    flexWrap: 'nowrap',
    overflowX: 'hidden',
    marginY: ['0', 'md'],
};

const FundManagersListItem = styled<any, any>('li')`
    ${propStyles};
    transition: transform 300ms ease-in-out;
    transform: ${p => `translate3d(${-100 * p.activeSlide}%, 0, 0)`};
`;

FundManagersListItem.defaultProps = {
    display: 'flex',
    flex: '0 0 auto',
    maxWidth: '100%',
    flexDirection: ['row', 'column'],
    alignItems: ['flex-start', 'stretch'],
    bgColor: 'white',
    borderWidth: '1px',
    borderStyle: ['none none solid none', 'solid'],
    borderColor: 'gray300',
    borderRadius: ['0', 'sm'],
    boxShadow: ['none', 'sm'],
    padding: 'md',
};

const FundManagerDetails = styled<any, any>('div')`
    ${propStyles};
`;

FundManagerDetails.defaultProps = {
    margin: ['0 0 0 1em', '0'],
    width: '100%',
};

const FundManagersNav = styled<any, any>('nav')`
    ${propStyles};
`;

FundManagersNav.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginY: 'md',
};

const FundManagersNavButton = styled<any, any>('button')`
    ${propStyles};
    ${isDisabled};
    outline: none;
    cursor: pointer;
    &:disabled {
        opacity: 0.25;
    }
`;

FundManagersNavButton.defaultProps = {
    bgColor: 'transparent',
    borderStyle: 'none',
    fontSize: 'lg',
    padding: '0 0.1em',
    color: 'gray500',
    selected: {
        color: 'body',
    },
};

interface FundManagersProps {
    theme?: any;
    dataStore?: DataStore;
    isMobile: boolean;
    showHeading: boolean;
    fundManagers: Array<FundManager>;
    fundManagersConfig: FundManagersConfig;
    imageSrc?: string;
}

@inject('dataStore')
@observer
class FundManagers extends Component<FundManagersProps> {
    autoPlayId: number;
    @observable activeSlide: number = 0;

    constructor(props) {
        super(props);

        if (!this.props.isMobile && this.autoPlayInterval > 0) {
            this.autoPlay(this.autoPlayInterval);
        }
    }

    componentWillUnmount() {
        clearInterval(this.autoPlayId);
    }

    @computed
    get showBiography(): boolean {
        const { showBiography } = this.props.fundManagersConfig;
        return showBiography;
    }

    @computed
    get showExtended(): Array<ShowExtended> {
        const { showExtended = [] } = this.props.fundManagersConfig;
        return showExtended;
    }

    @computed
    get link(): Link {
        const { link } = this.props.fundManagersConfig;
        return link;
    }

    @computed
    get isCarousel(): boolean {
        const { carousel = true } = this.props.fundManagersConfig;
        return carousel;
    }

    @computed
    get autoPlayInterval(): number {
        const { autoPlay = 0 } = this.props.fundManagersConfig;
        return this.isCarousel ? autoPlay : 0;
    }

    @computed
    get fundManagers() {
        return this.props.fundManagers.map(fm => {
            const fundManager = {
                code: fm.code,
                name: fm.name,
            } as any;

            if (this.showBiography) {
                const biographyData =
                    fm.biography.find(
                        bio => bio.culture === this.props.dataStore.culture
                    ) ||
                    fm.biography.find(
                        bio =>
                            bio.culture ===
                            this.props.dataStore.config.defaultCulture
                    );

                const { biography } = biographyData;

                fundManager.biography = biography;
            }

            if (this.showExtended.length > 0) {
                fundManager.extended = this.showExtended.map(extended => ({
                    label: extended.label,
                    value: this.getExtendedValue(
                        extended.property,
                        fm.extended
                    ),
                }));
            }

            if (this.link) {
                fundManager.link = {
                    label: this.link.label,
                    value: this.getExtendedValue(
                        this.link.property,
                        fm.extended
                    ),
                };
            }

            return fundManager;
        });
    }

    getExtendedValue = (
        property: string,
        extended: FundManagerExtended
    ): string => {
        return get(
            extended,
            `${property}.value`,
            get(extended, `${property}`, '')
        );
    };

    @action
    setActiveSlide = (index: number) => {
        this.activeSlide = index;
        this.autoPlay(this.autoPlayInterval);
    };

    @computed
    get prevSlideDisabled(): boolean {
        return this.activeSlide === 0;
    }

    @computed
    get nextSlideDisabled(): boolean {
        return this.activeSlide === this.fundManagers.length - 1;
    }

    @action
    prevSlide = () => {
        this.activeSlide = this.prevSlideDisabled
            ? this.fundManagers.length - 1
            : this.activeSlide - 1;
        this.autoPlay(this.autoPlayInterval);
    };

    @action
    nextSlide = () => {
        this.activeSlide = this.nextSlideDisabled ? 0 : this.activeSlide + 1;
        this.autoPlay(this.autoPlayInterval);
    };

    @action
    autoPlay = t => {
        if (t > 0) {
            if (this.autoPlayId) {
                clearInterval(this.autoPlayId);
            }
            this.autoPlayId = setInterval(this.nextSlide, t) as any;
        }
    };

    render() {
        const {
            theme,
            fundManagersConfig,
            showHeading,
            isMobile,
            imageSrc,
        } = this.props;
        const {
            fundManagers,
            setActiveSlide,
            prevSlide,
            nextSlide,
            activeSlide,
            prevSlideDisabled,
            nextSlideDisabled,
            isCarousel,
        } = this;

        return (
            <FundManagersWrapper {...theme.FundManagersWrapper}>
                {showHeading && (
                    <FundManagersHeading {...theme.FundManagersHeading}>
                        <Translate>{fundManagersConfig.label}</Translate>
                    </FundManagersHeading>
                )}
                <FundManagersList
                    flexDirection={isCarousel ? ['column', 'row'] : 'column'}
                    {...theme.FundManagersList}
                >
                    {!isMobile && isCarousel && fundManagers.length > 1 && (
                        <FundManagersNavArrows {...theme.FundManagersNavArrows}>
                            <FundManagersNavButton
                                onClick={() => prevSlide()}
                                disabled={prevSlideDisabled}
                                padding="sm"
                                {...theme.FundManagersNavArrowButton}
                            >
                                <Icon fileName="FundManagersNavArrowLeftIcon" />
                            </FundManagersNavButton>
                            <FundManagersNavButton
                                onClick={() => nextSlide()}
                                disabled={nextSlideDisabled}
                                padding="sm"
                                {...theme.FundManagersNavArrowButton}
                            >
                                <Icon fileName="FundManagersNavArrowRightIcon" />
                            </FundManagersNavButton>
                        </FundManagersNavArrows>
                    )}
                    {fundManagers.map((fundManager, index) => (
                        <FundManagersListItem
                            key={fundManager.code}
                            data-selected={activeSlide === index}
                            activeSlide={activeSlide}
                            {...theme.FundManagersListItem}
                        >
                            {fundManagersConfig.showImage && (
                                <FundManagerImage
                                    name={fundManager.name}
                                    code={fundManager.code}
                                    src={imageSrc}
                                />
                            )}
                            {isMobile ? (
                                <FundManagerDetails
                                    {...theme.FundManagerDetailsMobile}
                                >
                                    <FundManagerContent
                                        fundManager={fundManager}
                                    />
                                </FundManagerDetails>
                            ) : (
                                <FundManagerContent fundManager={fundManager} />
                            )}
                        </FundManagersListItem>
                    ))}
                </FundManagersList>
                {!isMobile && isCarousel && fundManagers.length > 1 && (
                    <FundManagersNav {...theme.FundManagersNav}>
                        {fundManagers.map((fm, index) => (
                            <FundManagersNavButton
                                key={fm.code}
                                onClick={() => setActiveSlide(index)}
                                data-selected={activeSlide === index}
                                {...theme.FundManagersNavButton}
                            >
                                {'●'}
                            </FundManagersNavButton>
                        ))}
                    </FundManagersNav>
                )}
            </FundManagersWrapper>
        );
    }
}

export default withTheme(FundManagers) as any;



// WEBPACK FOOTER //
// ./src/Components/FundManagers.tsx